import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { appendCrumb, normalizeTrail } from '../utils/entityTrail';

/**
 * Cross-entity breadcrumb trail, scoped to detail -> detail navigation only
 * (see Contexto in the entity-trail plan). Carried in `location.state.trail`,
 * so it survives reload/back-forward for free and needs no storage.
 *
 * A detail screen's `DetailBreadcrumb` registers itself as the current
 * "origin" (its own root + current crumb) via `useRegisterTrailOrigin`. Any
 * outbound `TrailLink` / `navigateWithTrail` call then appends onto the
 * trail using that origin — read only at click time, never during render,
 * so there's no dependency on effect-vs-render ordering.
 *
 * Out of scope by design: navigating through an editor (`returnTo`) or
 * pasting a detail URL directly both start a fresh (empty) trail.
 */
const EntityTrailContext = createContext(null);

export function EntityTrailProvider({ children }) {
	const location = useLocation();
	const navigate = useNavigate();
	const originRef = useRef(null);

	const trail = useMemo(() => normalizeTrail(location.state?.trail), [location.state]);

	const registerOrigin = useCallback((origin) => {
		originRef.current = origin;
	}, []);

	const clearOrigin = useCallback((origin) => {
		// Only clear if this registration still owns the ref — guards against a
		// stale unmount cleanup wiping a newer screen's origin.
		if (originRef.current === origin) {
			originRef.current = null;
		}
	}, []);

	const buildTrail = useCallback(
		(destinationTo) => {
			const origin = originRef.current;
			return origin ? appendCrumb(trail, origin, destinationTo) : null;
		},
		[trail],
	);

	const navigateWithTrail = useCallback(
		(to, options) => {
			const nextTrail = buildTrail(to);
			if (!nextTrail) {
				navigate(to, options);
				return;
			}
			navigate(to, { ...options, state: { ...options?.state, trail: nextTrail } });
		},
		[navigate, buildTrail],
	);

	const value = useMemo(
		() => ({ trail, registerOrigin, clearOrigin, buildTrail, navigateWithTrail }),
		[trail, registerOrigin, clearOrigin, buildTrail, navigateWithTrail],
	);

	return <EntityTrailContext.Provider value={value}>{children}</EntityTrailContext.Provider>;
}

/** Read the trail (already validated) for rendering a breadcrumb. */
export function useEntityTrail() {
	const ctx = useContext(EntityTrailContext);
	return { trail: ctx?.trail || [] };
}

/**
 * Registers `{ root, current }` as the trail origin for as long as the
 * calling component is mounted. `root`/`current` should be stable across
 * re-renders with the same values (a new object identity each render is
 * fine — this only writes a ref, it never schedules a re-render).
 */
export function useRegisterTrailOrigin(origin) {
	const ctx = useContext(EntityTrailContext);
	const label = origin?.current?.label;
	const to = origin?.current?.to;
	const rootLabel = origin?.root?.label;
	const rootTo = origin?.root?.to;

	useEffect(() => {
		if (!ctx || !to) {
			return undefined;
		}
		const registered = { root: { label: rootLabel, to: rootTo }, current: { label, to } };
		ctx.registerOrigin(registered);
		return () => ctx.clearOrigin(registered);
	}, [ctx, label, to, rootLabel, rootTo]);
}

/** navigate()-alike that appends the registered origin onto the trail. */
export function useTrailNavigate() {
	const ctx = useContext(EntityTrailContext);
	const navigate = useNavigate();
	return ctx?.navigateWithTrail || navigate;
}
